/*
  Scratch card popup (sections/scratch-popup.liquid).
  Kept out of theme.js on purpose: that file is a build artefact of sources that do not live in this repository.

  Flow: the prize is drawn once per visitor and remembered, the visitor scratches the canvas (or clicks "reveal"),
  leaves an email address, gets subscribed to the Klaviyo list with the prize as profile properties, and sees the code.
  The skin quiz gives the same prize: whichever the visitor fills in first claims it, and the other one only shows it.

  With Theme settings > Welcome offer > Code delivery set to email, the page carries no codes at all: the visitor sees
  "check your inbox" and the Klaviyo welcome flow sends a unique coupon for the amount in the profile property
  scratch_prize, one per profile, so a second device or a second signup with the same email gets nothing extra.
*/

// v2: prizes became fixed amounts (id based), older stored percentages must not match.
const PRIZE_KEY = 'cocovaye:scratch-prize-v2';
const FILLED_KEY = 'theme:popup-filled';
// One welcome offer per visitor, shared with the skin quiz (assets/skin-quiz.js reads it): once claimed here, the quiz
// shows this code instead of asking for the email again or handing out another one.
const OFFER_CLAIM_KEY = 'cocovaye:offer-claimed';
const REVEAL_THRESHOLD = 0.5;

// ---------------------------------------------------------------------------------------------------------------------
// Klaviyo signup errors. A failed signup gets a reason, reported to Clarity (event + tag) and the console, so a broken
// list ID or blocked request shows up in Clarity by name instead of as one generic error message:
// - invalid_email: Klaviyo rejected the address (e.g. "name@gmail" without .com)
// - invalid_list:  the list or account ID is wrong (see snippets/klaviyo-ids.liquid)
// - blocked:       the request never reached Klaviyo (ad blocker, privacy setting, offline)
// - http_<status>: any other refusal
// Same helper in assets/scratch-popup.js and assets/skin-quiz.js.
// ---------------------------------------------------------------------------------------------------------------------
class SignupError extends Error {
  constructor(reason, detail = '') {
    super(`Klaviyo signup failed: ${reason}${detail ? ` (${detail})` : ''}`);
    this.reason = reason;
  }
}

const klaviyoSubscribe = async (url, options) => {
  let response;
  try {
    response = await fetch(url, options);
  } catch {
    throw new SignupError('blocked');
  }
  if (response.ok) return;

  const error = await response.json().then((json) => json.errors?.[0] || {}).catch(() => ({}));
  const where = `${error.source?.pointer || ''} ${error.detail || ''}`.toLowerCase();
  const reason = where.includes('email') ? 'invalid_email' : /list|group|company/.test(where) ? 'invalid_list' : `http_${response.status}`;
  throw new SignupError(reason, error.detail);
};

const reportSignupError = (source, error) => {
  const reason = error?.reason || 'unknown';
  console.warn(`[${source}]`, error?.message || error);
  try {
    window.clarity?.('event', `signup_error_${reason}`);
    window.clarity?.('set', 'signup_error', `${source}: ${reason} (${window.Shopify?.locale || ''})`);
  } catch { /* Clarity not loaded */ }
};
const BRUSH_RADIUS = 22;
const CLOSE_DELAY = 3000;
const AUTO_REVEAL_DELAY = 2000;

const storage = {
  get(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, value); } catch { /* private mode, ignore */ }
  }
};

class ScratchCard extends HTMLElement {
  connectedCallback() {
    this.prizes = this.#parsePrizes();
    if (this.prizes.length === 0) return;

    this.prize = this.#pickPrize();
    // Shown to the visitor in their own currency; the Klaviyo properties keep the store currency label
    this.prize.displayLabel = this.#formatAmount(this.prize.amount, this.prize.label);
    this.#localizeHeading();
    this.canvas = this.querySelector('canvas');
    this.form = this.querySelector('[data-step="email"]');
    this.success = this.querySelector('[data-step="success"]');
    this.errorElement = this.querySelector('[data-form-error]');

    this.querySelector('[data-prize-label]').textContent = this.prize.displayLabel;
    this.querySelector('[data-reveal]').addEventListener('click', () => this.reveal());
    this.querySelector('[data-copy]')?.addEventListener('click', (event) => this.#copy(event.currentTarget));
    this.form.addEventListener('submit', (event) => this.#submit(event));

    this.#bindScratching();
    this.#delayCloseButton();

    // The popup is hidden on load, so the canvas has no size yet: paint it once it becomes visible.
    this.resizeObserver = new ResizeObserver(() => this.#paintCover());
    this.resizeObserver.observe(this.canvas);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    clearTimeout(this.closableTimeout);
    clearTimeout(this.autoRevealTimeout);
  }

  /**
   * The close button only appears 3 seconds after the popup opens, so the offer gets a moment before it can be
   * dismissed. Escape still closes it straight away, for keyboard and screen reader users.
   */
  #delayCloseButton() {
    const popup = this.closest('newsletter-popup');
    if (!popup) return;

    const arm = () => {
      clearTimeout(this.closableTimeout);
      popup.classList.remove('is-closable');
      this.closableTimeout = setTimeout(() => popup.classList.add('is-closable'), CLOSE_DELAY);
    };

    popup.addEventListener('dialog:before-show', arm);
    popup.addEventListener('dialog:before-hide', () => clearTimeout(this.closableTimeout));

    // In the theme editor, or if it is already open, start counting now
    if (popup.open) arm();
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.classList.add('is-revealed');

    // The headline becomes the result ("Your €5 credit is ready to claim"), keeping the amount with the word after it
    const winTitle = this.dataset.winTitle.replace(/__AMOUNT__ ?/, (match) => this.prize.displayLabel + (match.endsWith(' ') ? '\u00a0' : ''));
    const heading = this.querySelector('.scratch-card__heading');
    if (heading) heading.textContent = winTitle;
    this.querySelector('[data-win-title-target]').textContent = winTitle;
    this.form.hidden = false;

    // Wait for the canvas fade before moving focus, so the transition is not cut off on mobile keyboards.
    setTimeout(() => this.form.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 400);
  }

  #parsePrizes() {
    try {
      // With code delivery by email the page carries no codes: the welcome email sends a unique coupon for the amount
      const byEmail = this.dataset.delivery === 'email';
      return JSON.parse(this.dataset.prizes).filter((prize) => (byEmail || prize.code) && Number(prize.amount) > 0 && Number(prize.weight) > 0);
    } catch {
      return [];
    }
  }

  /**
   * The amounts are set in the store currency (EUR). Visitors who browse in another currency (the EU market sells in
   * local currencies) see them converted with Shopify's own rate, as the fixed-amount discount behind the code is
   * converted the same way at checkout. In the store currency the server-rendered label is kept as is.
   */
  #formatAmount(amount, fallback) {
    const currency = window.Shopify?.currency;
    const shopCurrency = this.dataset.shopCurrency;

    if (!currency?.active || !shopCurrency || currency.active === shopCurrency) return fallback;

    const value = Number(amount) * Number(currency.rate || 1);
    const whole = Math.abs(value - Math.round(value)) < 0.005;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency.active,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(whole ? Math.round(value) : value);
    } catch {
      return fallback;
    }
  }

  #localizeHeading() {
    this.querySelectorAll('[data-prize-amount]').forEach((element) => {
      element.textContent = this.#formatAmount(element.dataset.prizeAmount, element.textContent);
    });
  }

  #pickPrize() {
    const remembered = this.prizes.find((prize) => String(prize.id) === storage.get(PRIZE_KEY));
    if (remembered) return remembered;

    const total = this.prizes.reduce((sum, prize) => sum + Number(prize.weight), 0);
    let ticket = Math.random() * total;
    const prize = this.prizes.find((candidate) => (ticket -= Number(candidate.weight)) < 0) || this.prizes[0];

    storage.set(PRIZE_KEY, String(prize.id));
    return prize;
  }

  #paintCover() {
    const { clientWidth: width, clientHeight: height } = this.canvas;
    if (width === 0 || height === 0 || this.revealed || this.hasScratched) return;

    const ratio = window.devicePixelRatio || 1;
    this.canvas.width = width * ratio;
    this.canvas.height = height * ratio;

    const context = this.canvas.getContext('2d', { willReadFrequently: true });
    const styles = getComputedStyle(this.canvas);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.globalCompositeOperation = 'source-over';

    // Match the viewport-specific five-stop metallic gradient from the design.
    const coverAngle = Number.parseFloat(styles.getPropertyValue('--scratch-cover-angle')) || 246.7548;
    const angle = (coverAngle - 90) * Math.PI / 180;
    const direction = { x: Math.cos(angle), y: Math.sin(angle) };
    const extent = (Math.abs(direction.x) * width + Math.abs(direction.y) * height) / 2;
    const center = { x: width / 2, y: height / 2 };
    const gradient = context.createLinearGradient(
      center.x - direction.x * extent,
      center.y - direction.y * extent,
      center.x + direction.x * extent,
      center.y + direction.y * extent
    );
    gradient.addColorStop(0, styles.getPropertyValue('--scratch-cover-1').trim() || '#cdb48d');
    gradient.addColorStop(0.26247, styles.getPropertyValue('--scratch-cover-2').trim() || '#dfccae');
    gradient.addColorStop(0.52392, styles.getPropertyValue('--scratch-cover-3').trim() || '#f1e6d1');
    gradient.addColorStop(0.76828, styles.getPropertyValue('--scratch-cover-4').trim() || '#e9dbc2');
    gradient.addColorStop(1, styles.getPropertyValue('--scratch-cover-5').trim() || '#e3d2b5');
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    context.fillStyle = styles.getPropertyValue('--scratch-cover-text').trim() || '#604b40';
    const fontSize = styles.getPropertyValue('--scratch-cover-font-size').trim() || '32px';
    const fontFamily = styles.getPropertyValue('--scratch-cover-font-family').trim() || getComputedStyle(this).fontFamily;
    context.font = `700 ${fontSize} ${fontFamily}`;
    context.textAlign = 'center';

    // Center the letters themselves (cap height to baseline, like the trimmed text in Figma), not the em box.
    const coverText = this.canvas.dataset.coverText || '';
    const metrics = context.measureText(coverText);
    context.textBaseline = 'alphabetic';
    context.fillText(coverText, width / 2, height / 2 + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2);

    this.context = context;
  }

  #bindScratching() {
    let lastPoint = null;
    let moves = 0;

    const pointFor = (event) => {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    this.canvas.addEventListener('pointerdown', (event) => {
      if (!this.context || this.revealed) return;
      clearTimeout(this.autoRevealTimeout);
      this.canvas.setPointerCapture(event.pointerId);
      lastPoint = pointFor(event);
      this.#scratch(lastPoint, lastPoint);
    });

    this.canvas.addEventListener('pointermove', (event) => {
      if (!lastPoint) return;
      const point = pointFor(event);
      this.#scratch(lastPoint, point);
      lastPoint = point;

      if (++moves % 8 === 0 && this.#clearedRatio() >= REVEAL_THRESHOLD) {
        lastPoint = null;
        this.reveal();
      }
    });

    // Once the visitor has started scratching, a pause finishes the job: the card reveals itself after 2 seconds
    const stop = () => {
      if (!lastPoint) return;
      lastPoint = null;

      if (this.#clearedRatio() >= REVEAL_THRESHOLD) {
        this.reveal();
        return;
      }

      clearTimeout(this.autoRevealTimeout);
      this.autoRevealTimeout = setTimeout(() => this.reveal(), AUTO_REVEAL_DELAY);
    };

    this.canvas.addEventListener('pointerup', stop);
    this.canvas.addEventListener('pointercancel', stop);
  }

  #scratch(from, to) {
    this.hasScratched = true;
    const context = this.context;
    context.globalCompositeOperation = 'destination-out';
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = BRUSH_RADIUS * 2;
    context.beginPath();
    context.moveTo(from.x, from.y);
    context.lineTo(to.x + 0.01, to.y);
    context.stroke();
  }

  #clearedRatio() {
    const { width, height } = this.canvas;
    if (!width || !height) return 0;

    const pixels = this.context.getImageData(0, 0, width, height).data;
    const stride = 4 * 16;
    let cleared = 0;
    let sampled = 0;

    for (let index = 3; index < pixels.length; index += stride) {
      sampled++;
      if (pixels[index] === 0) cleared++;
    }

    return sampled ? cleared / sampled : 0;
  }

  async #submit(event) {
    event.preventDefault();

    const input = this.form.querySelector('input[type="email"]');
    const button = this.form.querySelector('[type="submit"]');
    const email = input.value.trim();

    if (!input.checkValidity() || !email) {
      input.reportValidity();
      return;
    }

    this.errorElement.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await this.#subscribe(email);
      storage.set(FILLED_KEY, 'true');
      storage.set(OFFER_CLAIM_KEY, JSON.stringify({ code: this.prize.code || null, amount: this.prize.amount, label: this.prize.label, displayLabel: this.prize.displayLabel }));
      // Lets Klaviyo's onsite script recognise the visitor from now on, so a later skin quiz lands on their profile
      try { window.klaviyo?.identify?.({ email }); } catch { /* Klaviyo blocked or not loaded */ }
      this.form.hidden = true;

      if (this.prize.code) {
        this.querySelector('[data-code]').textContent = this.prize.code;
        this.#applyCodeToShopLink();
      } else {
        const inbox = this.querySelector('[data-inbox-text]');
        if (inbox) inbox.textContent = inbox.dataset.inboxText.replace('__AMOUNT__', this.prize.displayLabel);
      }

      this.success.hidden = false;
    } catch (error) {
      reportSignupError('Scratch popup', error);
      this.errorElement.textContent = this.dataset.error;
      this.errorElement.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async #subscribe(email) {
    await klaviyoSubscribe(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/vnd.api+json',
        revision: '2026-07-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Scratch popup',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: {
                    scratch_prize: this.prize.amount,
                    scratch_prize_label: this.prize.label,
                    ...(this.prize.code ? { scratch_code: this.prize.code } : {})
                  }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: this.dataset.listId } }
          }
        }
      })
    });

  }

  // Route the shop button through /discount/<code> so the code is already applied when the visitor reaches the cart.
  #applyCodeToShopLink() {
    const link = this.success.querySelector('a[href]');
    if (!link) return;

    const target = new URL(link.getAttribute('href'), window.location.origin);
    const root = window.Shopify?.routes?.root || '/';
    link.href = `${root}discount/${encodeURIComponent(this.prize.code)}?redirect=${encodeURIComponent(target.pathname)}`;
  }

  async #copy(button) {
    try {
      await navigator.clipboard.writeText(this.prize.code);
      const original = button.textContent;
      button.textContent = this.dataset.copied;
      setTimeout(() => (button.textContent = original), 2000);
    } catch {
      /* Clipboard blocked: the code stays visible to copy by hand. */
    }
  }
}

if (!customElements.get('scratch-card')) {
  customElements.define('scratch-card', ScratchCard);
}
